import { ChangeDetectionStrategy, Component, Input, OnDestroy, OnInit, computed, signal } from '@angular/core';
import { MatDialog } from '@angular/material/dialog';
import { UntilDestroy, untilDestroyed } from '@ngneat/until-destroy';
import { filter, switchMap } from 'rxjs';
import { StackingPlanService } from '../stacking-plan.service';
import { StackingPlanReviewer } from '../stacking-plan.model';
import { AddLastReviewedComponent } from './add-last-reviewed/add-last-reviewed.component';

@UntilDestroy()
@Component({
  selector: 'sp-last-reviewed-by',
  templateUrl: './last-reviewed-by.component.html',
  styleUrls: ['./last-reviewed-by.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
  standalone: false,
})
export class LastReviewedByComponent implements OnInit, OnDestroy {
  @Input() set reviewerData(value: StackingPlanReviewer) {
    const { reviewedBy, reviewedOn } = value || {};
    if (!value || (!reviewedBy && !reviewedOn)) {
      this.reviewedInfo.set(null);
      return;
    }

    this.reviewedInfo.set({ reviewedBy, reviewedOn });
    this.selectedReviewer = value;
  }

  reviewedInfo = signal<StackingPlanReviewer>(null);
  derivedTooltipMsg = computed(() => this._updateTooltip(this.reviewedInfo()));

  tooltipMsg: string;
  hasEDPWriteAccess = false;

  private selectedReviewer: StackingPlanReviewer;

  constructor(
    private dialog: MatDialog,
    private stackingPlanService: StackingPlanService
  ) {}

  addReviewer(): void {
    const propertyId = this.stackingPlanService.propertyId;
    this.dialog
      .open(AddLastReviewedComponent, { minWidth: 650, autoFocus: false, data: this.selectedReviewer })
      .afterClosed()
      .pipe(
        untilDestroyed(this),
        filter(this._filterReviewerInfo),
        switchMap(reviewerInfo => this.stackingPlanService.updateLastReviewer(propertyId, reviewerInfo))
      )
      .subscribe(reviewerInfo => {
        this.selectedReviewer = reviewerInfo;
        this.reviewedInfo.set(reviewerInfo);
        this.stackingPlanService.updateLastReviewedData(reviewerInfo);
      });
  }

  private _updateTooltip = (reviewerInfo: StackingPlanReviewer): void => {
    const { reviewedBy, reviewedOn } = reviewerInfo || {};
    this.tooltipMsg = reviewedBy && reviewedOn ? 'commonLabels.editReviewer' : 'commonLabels.addReviewer';
  };

  private _filterReviewerInfo = (reviewerInfo: StackingPlanReviewer): boolean => {
    const { reviewedBy, reviewedOn } = reviewerInfo || {};
    const { reviewedBy: currentReviewer, reviewedOn: currentDate } = this.selectedReviewer || {};
    return !!reviewedBy && !!reviewedOn && (currentReviewer !== reviewedBy || currentDate !== reviewedOn);
  };

  ngOnInit(): void {
    this.hasEDPWriteAccess = this.stackingPlanService.hasEDPWriteAccess();
  }

  ngOnDestroy() {
    // * required by untilDestroy...
  }
}
